<template>
  <sb-table :column-definitions="pricingPlanColumnDefs" :rows="rows" :loading-promise="loadingPromise" :style="getStyleForTable()">
    <sb-table-thead slot="sb-table-header" slot-scope="{cols}">
      <sb-table-head
        v-for="(col, index) in cols"
        :key="col.name"
        :style="calculateStyleForHeader(index)"
      >
        <template v-if="col.field === 'weightRange'">
          {{ col.name }}
          <img
            id="table-calculator-icon"
            src="./assets/images/calculator.svg"
            @click="calculatorIconClick()"
          >
        </template>
        <template v-else-if="col.meta">
          <div style="display:flex;flex-direction: column;">
            <span>{{ col.name }}</span><span style="font-weight:normal">{{ col.meta }}</span>
          </div>
        </template>
        <template v-else>
          {{ col.name }}
        </template>
      </sb-table-head>
    </sb-table-thead>
    <sb-table-row slot="sb-table-row" slot-scope="{row}">
      <template v-if="!row.isThresholdRow">
        <sb-table-cell
          v-for="(col, index) in pricingPlanColumnDefs"
          :key="col.field + row.id"
          :style="calculateStyleForRow(index)"
        >
          <template v-if="index === 0">
            {{ displayWeightRange(row.weightRangeText) }}
          </template>
          <template v-else-if="showLocalCurrencyWithSymbolAndCode">
            {{ displayPriceV2(row[col.field]) | formatCurrency({currency: getCurrencyCodeForZonePrice(row[col.field]), showCurrencyCode: true, minimumFractionDigits: 2}) }}
          </template>
          <template v-else>
            {{ displayPrice(row[col.field]) }}
          </template>
        </sb-table-cell>
      </template>
      <template v-else>
        <td :colspan="pricingPlanColumnDefs.length" class="threshold-text">
          {{ row.thresholdText }}
        </td>
      </template>
    </sb-table-row>
  </sb-table>
</template>
<script>

import countryIsoCodes from '@/enums/modules/pricing/countryIsoCodes'
const borderGray = ' #d3d9e2'

export default {
  name: 'PricingTable',
  data: () => ({
    tableLoading: false
  }),
  computed: {
    pricingPlanColumnDefs () {
      const zones = this.pricingZones ? this.pricingZones : []
      const currencyText = this.showLocalCurrencyWithSymbolAndCode ? ' (' + this.selectedCountryCurrency + ')' : ''
      const mappedColumnDefs = zones.map(zone => {
        return {
          name: `Zone ${zone.name}` + currencyText,
          field: `zone${zone.id}`,
          meta: zone.description
        }
      })

      mappedColumnDefs.unshift({ name: 'Billable Weight', field: 'weightRange' })
      return mappedColumnDefs
    },
    loadingPromise () {
      return this.$shipBobStore.getters['pricing/loading']('table')
    },
    pricingPlan () {
      return this.$shipBobStore.getters['pricing/pricingPlan']
    },
    pricingZones () {
      return this.$shipBobStore.getters['pricing/zones']
    },
    unitOfMeasure () {
      return this.$shipBobStore.getters['pricing/unitOfMeasure']
    },
    rows () {
      const r = this.pricingPlan.pricingRates.map(pricingRate => {
        const { id, weightRange, weightRangeText, zonePrices } = pricingRate
        const mappedZonePrices = zonePrices.reduce((acc, zonePrice) => {
          const key = `zone${zonePrice.zoneId}`
          acc[key] = { price: zonePrice.price, currencyCode: zonePrice.currencyCode }
          return acc
        }, {})
        return {
          id,
          weightRange,
          weightRangeText,
          ...mappedZonePrices
        }
      })

      if (r.length && this.pricingPlan.pricingRates.length) {
        const hasDomesticDestination = this.pricingPlan.destinations.find((dest) => dest.isoCode === this.sourceCountryIso)
        const hasInternationalDestination = this.pricingPlan.destinations.find((dest) => dest.isoCode !== this.sourceCountryIso)

        const unitAbbreviation = this.unitOfMeasure.Abbreviation === 'lbs' ? 'lb' : this.unitOfMeasure.Abbreviation
        const weightThreshold = unitAbbreviation === 'lb' ? 1 : 16
        if (hasDomesticDestination) {
          let index = 1
          if (this.sourceCountryIso === 'US') {
            const foundIndex = this.pricingPlan.pricingRates.findIndex(pr => pr.weightRange[0] > weightThreshold || pr.weightRange[1] > weightThreshold)
            index = Math.max(1, foundIndex)
          }

          const thresholdText = this.getDomesticThresholdText(weightThreshold, unitAbbreviation)
          if (r[index - 1].isThresholdRow) {
            if (thresholdText !== r[index - 1].thresholdText) {
              r.splice(index - 1, 1, { isThresholdRow: true, thresholdText: `${r[0].thresholdText} ${thresholdText}` })
            }
          } else {
            r.splice(index - 1, 0, { isThresholdRow: true, thresholdText })
          }
        }

        if (hasInternationalDestination) {
          const thresholdText = this.getInternationalThresholdText()
          if (r[0].isThresholdRow) {
            if (thresholdText !== r[0].thresholdText) {
              r.splice(0, 1, { isThresholdRow: true, thresholdText: `${r[0].thresholdText} ${thresholdText}` })
            }
          } else {
            r.splice(0, 0, { isThresholdRow: true, thresholdText })
          }
        }
      }

      return r
    },
    pricingUpdated () {
      return this.$shipBobStore.getters['pricing/pricingUpdated']
    },
    shouldUseActualWeightForPricingEstimation () {
      return this.$shipBobStore.getters.useActualWeightForPricingEstimation
    },
    enableDimDivisorByCountry () {
      return this.$shipBobStore.getters.enableDimDivisorByCountry
    },
    sourceCountryIso () {
      return this.$shipBobStore.getters['pricing/preservedOriginCountry']
    },
    showLocalCurrencyWithSymbolAndCode () {
      return this.$shipBobStore.getters.showLocalCurrencyWithSymbolAndCode
    },
    selectedCountryCurrency () {
      return this.$shipBobStore.getters['pricing/selectedCountryCurrency']
    }
  },
  watch: {
    loadingPromise (loading) {
      this.tableLoading = true
      if (loading && loading.then) {
        loading.then(_ => {
          this.tableLoading = false
        })
      }
    }
  },
  methods: {
    async getPricingPlan (shipOptionId, zoneIds) {
      return this.$shipBobStore.dispatch('pricing/getPricingPlan', { shipOptionId, zoneIds })
    },
    displayPrice (zonePrice) {
      return zonePrice?.price ? '$' + zonePrice.price.toFixed(2) : '-'
    },
    displayPriceV2 (zonePrice) {
      return zonePrice?.price
    },
    getCurrencyCodeForZonePrice (zonePrice) {
      return zonePrice?.currencyCode
    },
    displayWeightRange (weightRangeText) {
      if (this.unitOfMeasure.Abbreviation === 'lbs') {
        return `${weightRangeText[0]} - ${weightRangeText[1]}`
      }

      return `${weightRangeText[0]} - ${weightRangeText[1]} ${this.unitOfMeasure.Abbreviation}`
    },
    calculateStyleForHeader (index) {
      if (index === 0) return { verticalAlign: 'middle', backgroundColor: 'rgba(241,243,246)', position: 'sticky', left: '-1px', top: '-1px', 'z-index': this.tableLoading ? 1 : 2, 'box-shadow': `1px 1px ${borderGray}` }
      return { verticalAlign: 'middle', position: 'sticky', backgroundColor: 'rgba(255,255,255)', top: '-1px', 'z-index': 1, 'box-shadow': `1px 1px ${borderGray}` }
    },
    calculateStyleForRow (index) {
      if (index === 0) return { backgroundColor: 'rgba(241,243,246)', position: 'sticky', left: '-1px', 'z-index': 1, 'box-shadow': `1px 1px ${borderGray}` }
    },
    getStyleForTable () {
      let style = { height: 'calc(100vh - 4rem)', border: `1px solid ${borderGray}` }
      if (!this.pricingUpdated) {
        style = { ...style, display: 'none' }
      }
      return style
    },
    calculatorIconClick () {
      this.$emit('calc-icon-click')
    },
    getDomesticThresholdText (weightThreshold, unitAbbreviation) {
      const sourceCountry = countryIsoCodes[this.sourceCountryIso]
      if (this.shouldUseActualWeightForPricingEstimation && this.enableDimDivisorByCountry) {
        if (this.sourceCountryIso === 'US') {
          return `All domestic ${sourceCountry} orders ${weightThreshold}${unitAbbreviation} or greater will be subject to dimensional weight. ${sourceCountry} territories are treated as international orders.`
        } else if (this.sourceCountryIso === 'PL' || this.sourceCountryIso === 'GB' || this.sourceCountryIso === 'IE') {
          return `All ${sourceCountry} orders will be subject to actual weight.`
        } else {
          return `All ${sourceCountry} orders will be subject to dimensional weight.`
        }
      } else if (this.enableDimDivisorByCountry) {
        if (this.sourceCountryIso === 'US') {
          return `All domestic ${sourceCountry} orders ${weightThreshold}${unitAbbreviation} or greater will be subject to dimensional weight. ${sourceCountry} territories are treated as international orders.`
        } else {
          return `All ${sourceCountry} orders will be subject to dimensional weight.`
        }
      } else {
        if (this.sourceCountryIso === 'US') {
          return `All U.S. orders ${weightThreshold}${unitAbbreviation} or greater will be subject to dimensional weight. U.S. territories are treated as non-U.S. orders.`
        } else {
          return 'All non-U.S. orders will be subject to dimensional weight.'
        }
      }
    },
    getInternationalThresholdText () {
      const sourceCountry = countryIsoCodes[this.sourceCountryIso]
      if (this.shouldUseActualWeightForPricingEstimation && this.enableDimDivisorByCountry) {
        if (this.sourceCountryIso === 'US') {
          return `Orders shipping from the ${sourceCountry} to international locations will be subject to dimensional weight.`
        } else if (this.sourceCountryIso === 'PL' || this.sourceCountryIso === 'GB' || this.sourceCountryIso === 'IE') {
          return `All ${sourceCountry} orders will be subject to actual weight.`
        } else {
          return `All ${sourceCountry} orders will be subject to dimensional weight.`
        }
      } else if (this.enableDimDivisorByCountry) {
        if (this.sourceCountryIso === 'US') {
          return `Orders shipping from the ${sourceCountry} to international locations will be subject to dimensional weight.`
        } else {
          return `All ${sourceCountry} orders will be subject to dimensional weight.`
        }
      } else {
        return 'All non-U.S. orders will be subject to dimensional weight.'
      }
    }
  }
}
</script>
